import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IonicModule, MenuController, ModalController } from '@ionic/angular';
import { BACKDROP } from '@ionic/core/dist/types/utils/overlays';
import { Conta, Login, LoginService } from 'src/app/services/login.service';
import { PerfilAcesso, PerfilService } from 'src/app/services/perfil.service';
import { LogoutModalPage } from './logout-modal/logout-modal.page'

export interface MenuItem {
  id: number | null; // null para módulos
  path: string | null; // null para módulos
  nome_path: string;
  modulo_path: string;
  possui_acesso: boolean;
  path_acesso_pai_id: number | null;
  filhos: MenuItem[];
  expanded?: boolean;
  isModulo?: boolean; // true se for um item de módulo (nível 0)
}

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],  
  standalone: false
})
export class MenuComponent implements OnInit {
  conta_logada: Conta = {} as Conta;
  acessoPainel: PerfilAcesso | undefined;
  
  // Estrutura hierárquica de menus
  menuItems: MenuItem[] = [];
  menuItemsMap: Map<number, MenuItem> = new Map();
  expandedMenus: Set<number | string> = new Set(); // Permite number ou string (para módulos)

  interval: ReturnType<typeof setInterval> | undefined;
  timeOutLogout: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private router: Router,
    private menuController: MenuController,
    private loginService: LoginService,
    private perfilService: PerfilService,
    private modalController: ModalController
  ) { }

  ngOnInit() {    
    this.carregaMenu();
    this.startIntervalCarregaMenu();
  }

  private startIntervalCarregaMenu() {
    this.interval = setInterval(
      () => {
        if (this.conta_logada.nome == undefined) {
          this.carregaMenu();
        }
      }, 1000
    );
  }

  private carregaMenu() {
    this.menuController.enable(false, 'menu_principal');
    this.loginService.getLogin().then(
      async (login: Login) => {
        if (login !== null) {
          this.conta_logada = login.conta;                    
          this.perfilService.getPerfilAcesso(login.conta.perfil_id).then(
            (perfilAcesso: PerfilAcesso[]) => {
              // Encontra o acesso ao painel
              this.acessoPainel = perfilAcesso.find(
                (el: PerfilAcesso) => {
                  return (el.modulo_path === 'Painel' && el.possui_acesso)
                }
              );
              
              // Constrói a estrutura hierárquica de menus
              this.menuItems = this.buildMenuHierarchy(perfilAcesso);
              
              // Adiciona manualmente o item "Painel" ao menu se não existir
              this.adicionaItemPainelManual();
              
              clearInterval(this.interval);
            }
          );
          // this.iniciaTempoLogout();
        }
      }
    );
  }

  /**
   * Constrói a estrutura hierárquica de menus a partir da lista plana de PerfilAcesso
   * Estrutura:
   * - Nível 0: Módulos (modulo_path)
   * - Nível 1: Itens com path_acesso_pai_id = NULL dentro de cada módulo
   * - Nível 2+: Itens com path_acesso_pai_id preenchido
   */
  private buildMenuHierarchy(perfilAcesso: PerfilAcesso[]): MenuItem[] {
    // Cria um mapa de TODOS os itens retornados pela API
    // IMPORTANTE: Usa path_acesso_id como chave, não perfil_acesso.id
    const todosItensMap = new Map<number, PerfilAcesso>();
    perfilAcesso.forEach(item => {
      const pathAcessoId = item.path_acesso_id || item.id;
      todosItensMap.set(pathAcessoId, item);
    });

    // Identifica quais itens devem ser incluídos:
    // 1. Itens com acesso direto
    // 2. Itens que são ancestrais de itens com acesso (mesmo sem acesso próprio)
    const itensComAcesso = perfilAcesso.filter(item => item.possui_acesso);
    const idsParaIncluir = new Set<number>();
    
    // Função recursiva para adicionar um item e todos os seus ancestrais
    // IMPORTANTE: Usa path_acesso_id para rastrear itens, não perfil_acesso.id
    const adicionarItemEAscendentes = (pathAcessoId: number | null | undefined) => {
      if (!pathAcessoId || idsParaIncluir.has(pathAcessoId)) {
        return; // Já foi incluído ou não existe
      }
      
      // Busca o item pelo path_acesso_id (agora é a chave do Map)
      const item = todosItensMap.get(pathAcessoId);
      
      if (!item) {
        return; // Item não existe na lista retornada
      }
      
      idsParaIncluir.add(pathAcessoId);
      
      // Adiciona recursivamente o pai usando path_acesso_id
      if (item.path_acesso_pai_id !== null && item.path_acesso_pai_id !== undefined) {
        adicionarItemEAscendentes(item.path_acesso_pai_id);
      }
    };
    
    // Adiciona todos os itens com acesso e seus ancestrais
    // Usa path_acesso_id ao invés de perfil_acesso.id
    itensComAcesso.forEach(item => {
      const pathAcessoId = item.path_acesso_id || item.id;
      adicionarItemEAscendentes(pathAcessoId);
    });

    // Cria o mapa de MenuItem apenas para itens que devem ser incluídos
    // IMPORTANTE: Usa path_acesso_id como chave (já está sendo usado no todosItensMap)
    this.menuItemsMap.clear();
    idsParaIncluir.forEach(pathAcessoId => {
      const item = todosItensMap.get(pathAcessoId);
      if (item) {
        this.menuItemsMap.set(pathAcessoId, {
          id: pathAcessoId, // Usa path_acesso_id, não perfil_acesso.id
          path: item.path,
          nome_path: item.nome_path,
          modulo_path: item.modulo_path,
          possui_acesso: item.possui_acesso,
          path_acesso_pai_id: item.path_acesso_pai_id,
          filhos: [],
          expanded: false,  // Todos começam fechados
          isModulo: false
        });
      }
    });
    

    // LÓGICA - 1ª, 2ª E 3ª CAMADAS:
    // 1ª camada: Apenas módulos (distinct de modulo_path) - ✅ NÃO MUDAR
    // 2ª camada: Apenas itens com path_acesso_pai_id = NULL, agrupados dentro do módulo
    // 3ª camada: Apenas itens com path_acesso_pai_id != NULL, agrupados dentro do item de nível 2
    
    // Primeiro, limpa todos os arrays de filhos
    this.menuItemsMap.forEach((menuItem) => {
      menuItem.filhos = [];
    });
    
    // Identifica módulos distintos apenas a partir de itens que têm acesso (ou são ancestrais de itens com acesso)
    // Assim, módulo pai só aparece se houver pelo menos um filho/neto acessível
    const modulosDistintos = new Set<string>();
    idsParaIncluir.forEach((pathAcessoId) => {
      const item = todosItensMap.get(pathAcessoId);
      if (item?.modulo_path) {
        modulosDistintos.add(item.modulo_path);
      }
    });

    const modulosOrdenados = Array.from(modulosDistintos)
      .sort((a, b) => a.localeCompare(b));
    
    // Cria estrutura - módulos com filhos de nível 2
    // Usa Map para garantir que cada módulo seja criado apenas UMA vez
    const modulosCriados = new Map<string, MenuItem>();
    const resultado: MenuItem[] = [];
    
    modulosOrdenados.forEach((modulo) => {
      // Verifica se o módulo já foi criado (evita duplicatas)
      if (modulosCriados.has(modulo)) {
        return;
      }
      
      // 2ª CAMADA: Busca itens com path_acesso_pai_id = NULL dentro deste módulo
      const itensNivel2: MenuItem[] = [];
      let itemPainelDireto: MenuItem | null = null; // Guarda o item "Painel" filtrado para criar botão direto
      
      this.menuItemsMap.forEach((menuItem) => {
        if (menuItem.modulo_path === modulo && 
            (menuItem.path_acesso_pai_id === null || menuItem.path_acesso_pai_id === undefined)) {
          // Filtra itens que têm o mesmo nome do módulo pai (evita "Painel -> Painel")
          if (menuItem.nome_path !== modulo) {
            itensNivel2.push(menuItem);
          } else if (modulo === 'Painel') {
            // Guarda o item "Painel" para criar um botão direto (mesmo sem path, vai usar 'home')
            itemPainelDireto = menuItem;
          }
        }
      });
      
      // Se não encontrou o item "Painel" no menuItemsMap, busca diretamente em perfilAcesso
      // Isso garante que o item apareça mesmo sem acesso direto
      if (modulo === 'Painel' && itemPainelDireto === null) {
        const itemPainelPerfilAcesso = perfilAcesso.find(
          (item) => item.modulo_path === modulo && 
                    item.nome_path === modulo &&
                    (item.path_acesso_pai_id === null || item.path_acesso_pai_id === undefined)
        );
        
        if (itemPainelPerfilAcesso) {
          const pathAcessoId = itemPainelPerfilAcesso.path_acesso_id || itemPainelPerfilAcesso.id;
          itemPainelDireto = {
            id: pathAcessoId,
            path: itemPainelPerfilAcesso.path,
            nome_path: itemPainelPerfilAcesso.nome_path,
            modulo_path: itemPainelPerfilAcesso.modulo_path,
            possui_acesso: true, // Força acesso para aparecer no menu
            path_acesso_pai_id: null,
            filhos: [],
            expanded: false,
            isModulo: false
          };
        }
      }
      
      // Ordena os itens de nível 2
      itensNivel2.sort((a, b) => a.nome_path.localeCompare(b.nome_path));
      
      // 3ª CAMADA: Para cada item de nível 2, busca apenas filhos que estão em idsParaIncluir (têm acesso)
      // Pai só deve aparecer se tiver pelo menos um filho/neto acessível
      itensNivel2.forEach((itemNivel2) => {
        const itensNivel3: MenuItem[] = [];

        perfilAcesso.forEach((item) => {
          const itemPathAcessoId = item.path_acesso_id || item.id;
          if (item.modulo_path === modulo &&
              item.path_acesso_pai_id === itemNivel2.id &&
              idsParaIncluir.has(itemPathAcessoId)) {
            const menuItemFilho = this.menuItemsMap.get(itemPathAcessoId);
            if (menuItemFilho) {
              itensNivel3.push(menuItemFilho);
            }
          }
        });

        // Ordena os itens de nível 3
        itensNivel3.sort((a, b) => a.nome_path.localeCompare(b.nome_path));

        // Associa os filhos de nível 3 ao item de nível 2
        if (!itemNivel2.filhos) {
          itemNivel2.filhos = [];
        }
        itemNivel2.filhos.length = 0;
        itemNivel2.filhos.push(...itensNivel3);
      });

      // Remove itens de nível 2 que não têm nenhum filho acessível nem acesso próprio (pai vazio)
      const itensNivel2Visiveis = itensNivel2.filter(
        (item) => item.filhos.length > 0 || item.possui_acesso
      );
      
      // Verifica se há um item "Painel" que deve ser mantido como botão direto
      // Se o módulo é "Painel" e há um item "Painel" filtrado, cria um botão direto (sem filhos)
      // Isso mantém o botão "Painel" no menu sem criar "Painel -> Painel"
      let itemModulo: MenuItem;
      
      if (modulo === 'Painel' && itemPainelDireto !== null) {
        // Cria um botão direto "Painel" (sem filhos, mesmo que existam outros itens no módulo)
        // Se não tiver path, redireciona para home
        const itemPainel = itemPainelDireto as MenuItem; // Type assertion para garantir o tipo
        const pathPainel: string = (itemPainel.path !== null && itemPainel.path !== undefined && itemPainel.path !== '') 
          ? itemPainel.path 
          : 'home';
        itemModulo = {
          id: itemPainel.id,
          path: pathPainel,
          nome_path: modulo,
          modulo_path: modulo,
          possui_acesso: true, // Garante que tenha acesso para aparecer no menu
          path_acesso_pai_id: null,
          filhos: [],
          expanded: false,
          isModulo: false // Não é módulo, é um item direto clicável
        };
      } else {
        // Cria item de módulo só se houver pelo menos um filho visível (nível 2 com acesso ou com netos acessíveis)
        if (itensNivel2Visiveis.length === 0) {
          return; // Módulo sem nenhum item acessível: não exibe o pai
        }
        itemModulo = {
          id: null,
          path: null,
          nome_path: modulo,
          modulo_path: modulo,
          possui_acesso: true,
          path_acesso_pai_id: null,
          filhos: itensNivel2Visiveis,
          expanded: false,
          isModulo: true
        };
      }

      modulosCriados.set(modulo, itemModulo);
      resultado.push(itemModulo);
    });

    return resultado;
  }

  /**
   * Adiciona manualmente o item "Painel" ao menu se não existir
   * Garante que o botão "Painel" sempre apareça no menu
   */
  private adicionaItemPainelManual(): void {
    // Verifica se já existe um item "Painel" no menu
    const existePainel = this.menuItems.some(
      (item) => item.modulo_path === 'Painel' && item.nome_path === 'Painel'
    );
    
    // Se não existe, adiciona manualmente
    if (!existePainel) {
      const itemPainel: MenuItem = {
        id: null,
        path: 'home',
        nome_path: 'Painel',
        modulo_path: 'Painel',
        possui_acesso: true,
        path_acesso_pai_id: null,
        filhos: [],
        expanded: false,
        isModulo: false // Não é módulo, é um item direto clicável
      };
      
      // Adiciona ao início do array para aparecer primeiro
      this.menuItems.unshift(itemPainel);
    }
  }

  /**
   * Ordena recursivamente os itens do menu e seus filhos
   */
  private sortMenuItems(items: MenuItem[]): void {
    items.forEach(item => {
      if (item.filhos.length > 0) {
        item.filhos.sort((a, b) => a.nome_path.localeCompare(b.nome_path));
        this.sortMenuItems(item.filhos);
      }
    });
  }

  /**
   * Alterna a expansão de um item de menu
   */
  toggleMenu(item: MenuItem): void {
    if (item.filhos && item.filhos.length > 0) {
      // Toggle do estado expandido
      const estadoAnterior = item.expanded || false;
      item.expanded = !estadoAnterior;
      
      // Atualiza o Set de menus expandidos
      // Usa id se disponível, senão usa uma chave única baseada no nome
      const menuKey = item.id !== null ? item.id : `modulo_${item.modulo_path}`;
      
      if (item.expanded) {
        this.expandedMenus.add(menuKey);
      } else {
        this.expandedMenus.delete(menuKey);
        // Opcional: fechar todos os filhos quando o pai é fechado
        this.fecharFilhos(item);
      }
    }
  }

  /**
   * Fecha recursivamente todos os filhos de um item
   */
  private fecharFilhos(item: MenuItem): void {
    item.filhos.forEach(filho => {
      if (filho.expanded) {
        filho.expanded = false;
        const filhoKey = filho.id !== null ? filho.id : `modulo_${filho.modulo_path}`;
        this.expandedMenus.delete(filhoKey);
        if (filho.filhos.length > 0) {
          this.fecharFilhos(filho);
        }
      }
    });
  }

  /**
   * Verifica se um item de menu está expandido
   */
  isExpanded(item: MenuItem): boolean {
    return item.expanded || false;
  }

  /**
   * Verifica se um item tem filhos
   */
  hasChildren(item: MenuItem): boolean {
    return item.filhos && item.filhos.length > 0;
  }

  /**
   * Retorna o ícone apropriado para cada módulo
   */
  getIconForModulo(modulo: string): string {
    const iconMap: { [key: string]: string } = {
      'Listagem': 'list-outline',
      'Cadastro': 'create-outline',
      'Auditoria': 'document-text-outline',
      'Fiscalização': 'document-text-outline',
      'Relatório': 'document-text-outline',
      'Email': 'mail-outline',
      'Email/Ofícios': 'mail-outline',
      'Treinamento': 'school-outline',
      'Configuração': 'cog-outline',
      'Painel': 'apps-outline'
    };
    return iconMap[modulo] || 'ellipse-outline';
  }

  private iniciaTempoLogout() {
    // if (this.conta_logada.nome != undefined) {
    //   let i = 0;
    //   this.timeOutLogout = setInterval(
    //     () => {
    //       this.showModalLogoff();
    //       i++;
    //     }, 600000
    //   );
    // }
  }

  private restartTempoLogout() {
    // clearTimeout(this.timeOutLogout);
    // this.iniciaTempoLogout();
  }

  async showModalLogoff() {
    clearTimeout(this.timeOutLogout);
    const modal = await this.modalController.create({
      component: LogoutModalPage,
      cssClass: "modal-logout",
      componentProps: {
        contaLogada: this.conta_logada
      },
    });

    modal.onDidDismiss().then(async (params) => {
      if (params !== null) {
        if (params.data != undefined) {
          if (params.data.efetua_logout) {
            this.destroyLogin();
            this.router.navigate(["login"])
          } else {
            this.restartTempoLogout();
          }
        } else if (params.role == 'backdrop') {
          this.restartTempoLogout();
        }
      }
    });
    return await modal.present();
  }


  pushPage(rota: String) {
    this.restartTempoLogout();
    // Mapeia paths do path_acesso para rotas reais do frontend
    const rotaMapeada = this.mapearRota(rota);
    this.router.navigate([rotaMapeada], { skipLocationChange: true })
    this.menuController.close();
  }

  /**
   * Mapeia paths do path_acesso (banco de dados) para rotas reais do frontend
   * Os paths no banco mantêm o prefixo "email-" mas as rotas reais podem ter nomes diferentes
   */
  private mapearRota(path: String): string {
    const mapeamento: { [key: string]: string } = {
      'email-fiscalizacao-notificacao-diversas': 'fiscalizacao-notificacao-diversas'
    };
    
    return mapeamento[path.toString()] || path.toString();
  }

  destroyLogin() {
    this.menuController.enable(false, 'menu_principal');
    this.menuController.close('menu_principal');
    this.conta_logada.nome = undefined;
    this.startIntervalCarregaMenu();
    this.loginService.destroyLogin();
  }

  pushPainelUsuarioLogado() {
    this.menuController.close('menu_principal');
    this.router.navigate(["usuario-painel"], {
      queryParams: { id: this.conta_logada.usuario_id },
      skipLocationChange: true
    });
  }

  logout() {
    this.destroyLogin();
    //this.router.navigateByUrl('/login', { skipLocationChange: true });
    window.location.href = '/login';
  }

}
